import { useState, useEffect } from 'react';

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    const cookiesAccepted = localStorage.getItem('cookiesAccepted');
    if (!cookiesAccepted) {
      setShowBanner(true);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem('cookiesAccepted', 'all');
    localStorage.setItem('cookiePreferences', JSON.stringify({
      necessary: true,
      analytics: true,
      marketing: true
    }));
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    localStorage.setItem('cookiesAccepted', 'necessary');
    localStorage.setItem('cookiePreferences', JSON.stringify({
      necessary: true,
      analytics: false,
      marketing: false
    }));
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookiesAccepted', 'custom');
    localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleTogglePreference = (key: 'analytics' | 'marketing') => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!showBanner) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-[9998]" style={{ display: showBanner ? 'block' : 'none' }}></div>
      
      <div className="fixed bottom-0 left-0 right-0 z-[9999] bg-white shadow-2xl border-t-2 border-[#2EE6A6]">
        <div className="container mx-auto px-6 py-6 max-w-7xl">
          {!showSettings ? (
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6">
              <div className="flex-1">
                <div className="flex items-start gap-3 mb-3">
                  <i className="ri-cookie-line text-3xl text-[#2EE6A6] flex-shrink-0"></i>
                  <div>
                    <h3 className="text-lg font-bold text-[#0B3A53] mb-2">Utilizamos cookies</h3>
                    <p className="text-sm text-[#0F172A]/80 leading-relaxed">
                      Usamos cookies propias y de terceros para mejorar tu experiencia de navegación, analizar el tráfico del sitio y personalizar el contenido. Puedes aceptar todas las cookies, rechazarlas o configurar tus preferencias.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                <button
                  onClick={() => setShowSettings(true)}
                  className="px-6 py-3 bg-[#F6F7FB] text-[#0B3A53] rounded-lg font-semibold hover:bg-[#CBD5E1] transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  Configurar
                </button>
                <button
                  onClick={handleRejectAll}
                  className="px-6 py-3 bg-[#F6F7FB] text-[#0B3A53] rounded-lg font-semibold hover:bg-[#CBD5E1] transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  Rechazar todas
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-6 py-3 bg-[#2EE6A6] text-[#0F172A] rounded-lg font-bold hover:scale-105 transition-transform cursor-pointer whitespace-nowrap text-sm"
                >
                  Aceptar todas
                </button>
              </div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-[#0B3A53]">Configuración de cookies</h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F6F7FB] transition-colors cursor-pointer"
                  aria-label="Cerrar configuración"
                >
                  <i className="ri-close-line text-xl text-[#0F172A]"></i>
                </button>
              </div>

              <div className="space-y-4 mb-6">
                <div className="bg-[#F6F7FB] rounded-lg p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h4 className="font-bold text-[#0B3A53] mb-1">Cookies necesarias</h4>
                      <p className="text-sm text-[#0F172A]/70">
                        Esenciales para el funcionamiento del sitio. No se pueden desactivar.
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <div className="w-12 h-6 bg-[#2EE6A6] rounded-full flex items-center px-1 cursor-not-allowed opacity-50">
                        <div className="w-4 h-4 bg-white rounded-full transform translate-x-6"></div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-[#F6F7FB] rounded-lg p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h4 className="font-bold text-[#0B3A53] mb-1">Cookies analíticas</h4>
                      <p className="text-sm text-[#0F172A]/70">
                        Nos ayudan a entender cómo los visitantes interactúan con el sitio web.
                      </p>
                    </div>
                    <button
                      onClick={() => handleTogglePreference('analytics')}
                      className={`flex-shrink-0 w-12 h-6 rounded-full flex items-center px-1 transition-colors cursor-pointer ${
                        preferences.analytics ? 'bg-[#2EE6A6]' : 'bg-[#CBD5E1]'
                      }`}
                      aria-label="Activar cookies analíticas"
                    >
                      <div
                        className={`w-4 h-4 bg-white rounded-full transform transition-transform ${
                          preferences.analytics ? 'translate-x-6' : 'translate-x-0'
                        }`}
                      ></div>
                    </button>
                  </div>
                </div>

                <div className="bg-[#F6F7FB] rounded-lg p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h4 className="font-bold text-[#0B3A53] mb-1">Cookies de marketing</h4>
                      <p className="text-sm text-[#0F172A]/70">
                        Se utilizan para rastrear visitantes en los sitios web para mostrar anuncios relevantes.
                      </p>
                    </div>
                    <button
                      onClick={() => handleTogglePreference('marketing')}
                      className={`flex-shrink-0 w-12 h-6 rounded-full flex items-center px-1 transition-colors cursor-pointer ${
                        preferences.marketing ? 'bg-[#2EE6A6]' : 'bg-[#CBD5E1]'
                      }`}
                      aria-label="Activar cookies de marketing"
                    >
                      <div
                        className={`w-4 h-4 bg-white rounded-full transform transition-transform ${
                          preferences.marketing ? 'translate-x-6' : 'translate-x-0'
                        }`}
                      ></div>
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-end">
                <button
                  onClick={() => setShowSettings(false)}
                  className="px-6 py-3 bg-[#F6F7FB] text-[#0B3A53] rounded-lg font-semibold hover:bg-[#CBD5E1] transition-colors cursor-pointer whitespace-nowrap"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSavePreferences}
                  className="px-6 py-3 bg-[#2EE6A6] text-[#0F172A] rounded-lg font-bold hover:scale-105 transition-transform cursor-pointer whitespace-nowrap"
                >
                  Guardar preferencias
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CookieBanner;
